import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { schoolApi } from '../../api/school'

/**
 * Дашборд LLM-расходов школы за период. Доступен VICE_PRINCIPAL+.
 * Показывает суммарные счётчики + per-teacher breakdown с топ-N.
 */
export function SchoolLlmStats({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation()
  const [days, setDays] = useState(30)
  const q = useQuery({
    queryKey: ['school', 'llm-stats', schoolId, days],
    queryFn: () => schoolApi.llmStats(schoolId, days),
    retry: 1,
  })

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <h2 className="font-semibold text-gray-900">{t('schoolLlmStats.title')}</h2>
        <select
          className="input-field max-w-[140px] text-sm py-1"
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
        >
          <option value="7">{t('schoolLlmStats.period7')}</option>
          <option value="30">{t('schoolLlmStats.period30')}</option>
          <option value="90">{t('schoolLlmStats.period90')}</option>
          <option value="365">{t('schoolLlmStats.period365')}</option>
        </select>
      </div>

      {q.isLoading && <div className="text-sm text-gray-500">{t('common.loading')}</div>}
      {q.isError && (
        <div className="text-sm text-red-600">{t('schoolLlmStats.loadError')}</div>
      )}

      {q.data && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Metric
              label={t('schoolLlmStats.requests')}
              value={q.data.totalRequests.toLocaleString('ru')}
              hint={t('schoolLlmStats.daysHint', { n: days })}
            />
            <Metric
              label={t('schoolLlmStats.promptTokens')}
              value={q.data.totalPromptTokens.toLocaleString('ru')}
            />
            <Metric
              label={t('schoolLlmStats.completionTokens')}
              value={q.data.totalCompletionTokens.toLocaleString('ru')}
            />
          </div>

          {q.data.perTeacher.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-4">
              {t('schoolLlmStats.emptyPeriod')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
                    <th className="text-left py-2 px-1 font-normal">{t('schoolLlmStats.colTeacher')}</th>
                    <th className="text-right py-2 px-1 font-normal">{t('schoolLlmStats.requests')}</th>
                    <th className="text-right py-2 px-1 font-normal hidden md:table-cell">{t('schoolLlmStats.colPrompt')}</th>
                    <th className="text-right py-2 px-1 font-normal hidden md:table-cell">{t('schoolLlmStats.colCompletion')}</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.perTeacher.map((t) => (
                    <tr key={t.teacherId} className="border-b border-gray-100">
                      <td className="py-2 px-1">
                        <div className="font-medium text-gray-900">{t.displayName}</div>
                        <div className="text-xs text-gray-500">{t.email}</div>
                      </td>
                      <td className="text-right font-bold text-primary">{t.requestCount}</td>
                      <td className="text-right text-gray-700 hidden md:table-cell">
                        {t.promptTokens.toLocaleString('ru')}
                      </td>
                      <td className="text-right text-gray-700 hidden md:table-cell">
                        {t.completionTokens.toLocaleString('ru')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Metric({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint?: string
}) {
  return (
    <div className="bg-primary-soft/40 border border-purple-100 rounded-lg p-3">
      <div className="text-xs uppercase tracking-wider text-gray-500 truncate">{label}</div>
      <div className="text-2xl font-bold text-primary mt-1">{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
    </div>
  )
}
